import { ACCOUNT_PERMISSION_CATALOG, ORG_PERMISSION_CATALOG } from '@common/enums';
import type {
  AccountRoleTemplate,
  PermissionAction,
  PermissionCatalog,
  Role,
  RolePermissions,
} from '@common/types';

function fullPermissions(catalog: PermissionCatalog): RolePermissions {
  const map: Record<string, ReadonlyArray<PermissionAction>> = {};
  for (const group of catalog) {
    for (const resource of group.resources) {
      map[resource.key] = [...resource.actions];
    }
  }
  return map;
}

function viewOnlyPermissions(catalog: PermissionCatalog): RolePermissions {
  const map: Record<string, ReadonlyArray<PermissionAction>> = {};
  for (const group of catalog) {
    for (const resource of group.resources) {
      if (resource.actions.includes('view')) map[resource.key] = ['view'];
    }
  }
  return map;
}

const accountFull = fullPermissions(ACCOUNT_PERMISSION_CATALOG);

const accountAdmin: RolePermissions = {
  ...accountFull,
  users: ['view', 'create', 'edit'],
  'account-preferences': ['view', 'edit'],
};

const accountStandard: RolePermissions = {
  'manage-guests': ['view', 'create', 'edit'],
  unsubscribe: ['view', 'edit'],
  'exception-list': ['view'],
  reviews: ['view', 'edit'],
  'service-tickets': ['view', 'create', 'edit'],
  'reports-service-rating': ['view'],
  'reports-comprehensive': ['view'],
  'social-sharing': ['view'],
};

const orgFull = fullPermissions(ORG_PERMISSION_CATALOG);

const orgAdmin: RolePermissions = { ...orgFull, billing: ['view'] };

const orgBilling: RolePermissions = {
  accounts: ['view'],
  members: ['view'],
  billing: ['view', 'manage'],
  'org-settings': ['view'],
};

/**
 * Account system roles are materialised per account, so their ids are derived
 * rather than stored. Anything that needs to point at one (membership seeds,
 * access checks) composes the id here so the format lives in a single place.
 */
export function accountSystemRoleId(templateId: string, accountId: string): string {
  return `role-${templateId}__${accountId}`;
}

export const ACCOUNT_SYSTEM_ROLE_TEMPLATES: ReadonlyArray<AccountRoleTemplate> = [
  {
    id: 'acc-owner',
    name: 'Account Owner',
    description: 'Full control over the account, its users, and every operational area.',
    permissions: accountFull,
  },
  {
    id: 'acc-administrator',
    name: 'Administrator',
    description: 'Manages day-to-day operations and most settings, with limited user controls.',
    permissions: accountAdmin,
  },
  {
    id: 'acc-standard',
    name: 'Standard User',
    description: 'Handles guests, reviews, and tickets without access to account administration.',
    permissions: accountStandard,
  },
  {
    id: 'acc-readonly',
    name: 'Read Only',
    description: 'Can view operational data across the account but cannot make changes.',
    permissions: viewOnlyPermissions(ACCOUNT_PERMISSION_CATALOG),
  },
];

export const ORG_ROLES_SEED: ReadonlyArray<Role> = [
  {
    id: 'role-org-owner',
    scope: 'organization',
    accountId: null,
    name: 'Organization Owner',
    description: 'Unrestricted access across every account, billing, and organization setting.',
    system: true,
    permissions: orgFull,
    memberIds: ['usr-aiden'],
    createdAtIso: '2025-01-05T08:00:00.000Z',
    updatedAtIso: '2025-01-05T08:00:00.000Z',
  },
  {
    id: 'role-org-admin',
    scope: 'organization',
    accountId: null,
    name: 'Organization Admin',
    description: 'Manages accounts, members, and roles across the organization.',
    system: true,
    permissions: orgAdmin,
    memberIds: ['usr-sarah'],
    createdAtIso: '2025-01-05T08:00:00.000Z',
    updatedAtIso: '2025-02-11T10:05:00.000Z',
  },
  {
    id: 'role-org-billing',
    scope: 'organization',
    accountId: null,
    name: 'Billing Manager',
    description: 'Owns billing and plan management with read access to the rest of the organization.',
    system: true,
    permissions: orgBilling,
    memberIds: ['usr-james'],
    createdAtIso: '2025-01-05T08:00:00.000Z',
    updatedAtIso: '2025-01-05T08:00:00.000Z',
  },
  {
    id: 'role-org-readonly',
    scope: 'organization',
    accountId: null,
    name: 'Read Only',
    description: 'Can view organization-level data but cannot make any changes.',
    system: true,
    permissions: viewOnlyPermissions(ORG_PERMISSION_CATALOG),
    memberIds: [],
    createdAtIso: '2025-01-05T08:00:00.000Z',
    updatedAtIso: '2025-01-05T08:00:00.000Z',
  },
];
